import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

interface NonFsdsFaqSectionProps {
  title: string
  faqs: Array<{ id: string; q: string; a: string }>
  theme?: NonFsdsTheme | string
}

export default function NonFsdsFaqSection({ title, faqs, theme = 'general' }: NonFsdsFaqSectionProps) {
  const themeStyles = {
    general: {
      sectionBg: 'from-blue-50/50', underline: 'bg-blue-500',
      detailsBorder: 'border-blue-100/80 hover:border-blue-200',
      iconBg: 'bg-blue-50 text-blue-600 group-open:bg-blue-600',
      answerBg: 'border-blue-50 bg-blue-50/30',
    },
    'english-academy': {
      sectionBg: 'from-blue-50/40', underline: 'bg-blue-500',
      detailsBorder: 'border-blue-100/80 hover:border-emerald-200',
      iconBg: 'bg-blue-50 text-blue-600 group-open:bg-blue-600',
      answerBg: 'border-emerald-50 bg-emerald-50/30',
    },
    programming: {
      sectionBg: 'from-indigo-50/50', underline: 'bg-indigo-500',
      detailsBorder: 'border-indigo-100/80 hover:border-indigo-200',
      iconBg: 'bg-indigo-50 text-indigo-600 group-open:bg-indigo-600',
      answerBg: 'border-indigo-50 bg-indigo-50/30',
    },
    math: {
      sectionBg: 'from-teal-50/50', underline: 'bg-teal-500',
      detailsBorder: 'border-teal-100/80 hover:border-teal-200',
      iconBg: 'bg-teal-50 text-teal-600 group-open:bg-teal-600',
      answerBg: 'border-teal-50 bg-teal-50/30',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section data-testid="non-fsds-faq-section" className={`bg-gradient-to-b to-white px-4 py-16 sm:px-6 sm:py-20 lg:py-24 ${themeStyles.sectionBg}`}>
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{title}</h2>
          <div className={`mx-auto mt-6 h-1 w-20 rounded-full ${themeStyles.underline}`} />
        </div>
        <div className="mx-auto mt-12 max-w-3xl space-y-4">
          {faqs.map(faq => (
            <details key={faq.id} className={`group overflow-hidden rounded-[20px] border bg-white shadow-sm transition-all duration-300 open:shadow-md hover:shadow-md ${themeStyles.detailsBorder}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-6 text-lg font-bold text-slate-900 marker:content-none sm:p-8">
                {faq.q}
                <span className={`flex size-10 shrink-0 items-center justify-center rounded-full transition-transform duration-300 group-open:rotate-180 group-open:text-white ${themeStyles.iconBg}`}>
                  <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                </span>
              </summary>
              <div className={`border-t px-6 pb-8 pt-6 sm:px-8 ${themeStyles.answerBg}`}>
                <p className="text-base leading-relaxed text-slate-600 sm:text-lg">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
